import { useState } from "react";
import AuthNavbar from "../../components/navigations/AuthNavbar";
import PaymentMethodsList from "../../components/agents/commission/PaymentMethodsList";
import PayoutHistory from "../../components/agents/commission/PayoutHistory";
import PayoutSection from "../../components/agents/commission/PayoutSection";
import SetupModal from "../../components/agents/commission/SetupModal";
import EditModal from "../../components/agents/commission/EditModal";
import DeleteConfirmModal from "../../components/agents/commission/DeleteConfirmModal";
import ConfirmationModal from "../../components/agents/commission/ConfirmationModal";
import SuccessModal from "../../components/agents/commission/SuccessModal";
import useAccountStatusCheck from "../../hooks/useAccountStatusCheck";
import { useNavigate } from "react-router-dom";

export default function CommissionWithdrawal() {
  const navigate = useNavigate();
  
  // Check account status and show blocking modal if under review
  const { isBlocked, AccountStatusModal } = useAccountStatusCheck(true, () => {
    navigate("/dashboard");
  });
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedYear, setSelectedYear] = useState("2023");
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    momoType: "",
    phoneNumber: "",
    providerName: "",
    walletName: "",
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // New states for edit/delete functionality
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingMethod, setDeletingMethod] = useState(null);

  const momoProviders = [
    { id: "mtn", name: "MTN Mobile Money", color: "bg-yellow-500", icon: "📱" },
    { id: "vodafone", name: "Vodafone Cash", color: "bg-red-500", icon: "📱" },
    { id: "airtel", name: "AirtelTigo Money", color: "bg-red-600", icon: "📱" },
  ];

  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      type: "Mobile Money",
      provider: "MTN Mobile Money",
      number: "+233 24 123 4567",
      walletName: "Personal Wallet",
      isDefault: true,
      status: "active",
    },
    {
      id: 2,
      type: "Mobile Money",
      provider: "Vodafone Cash",
      number: "+233 50 987 6543",
      walletName: "Business Account",
      isDefault: false,
      status: "active",
    },
  ]);

  const payoutHistory = [
    {
      id: 1,
      type: "Reward Deposit",
      amount: "$45.20",
      date: "Dec 15, 2023",
      status: "Completed",
    },
    {
      id: 2,
      type: "Reward Transfer",
      amount: "$32.80",
      date: "Dec 10, 2023",
      status: "Completed",
    },
    {
      id: 3,
      type: "Reward Deposit",
      amount: "$28.50",
      date: "Dec 5, 2023",
      status: "Pending",
    },
  ];

  const handleSetupClick = () => {
    setShowSetupModal(true);
    setCurrentStep(1);
    setFormData({
      momoType: "",
      phoneNumber: "",
      providerName: "",
      walletName: "",
    });
  };

  const handleNext = () => {
    if (currentStep === 1 && formData.momoType) {
      setCurrentStep(2);
    } else if (
      currentStep === 2 &&
      formData.phoneNumber &&
      formData.walletName
    ) {
      setShowConfirmation(true);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    setShowSetupModal(false);

    // Add new payment method
    const newMethod = {
      id: Date.now(),
      type: "Mobile Money",
      provider: formData.providerName,
      number: formData.phoneNumber,
      walletName: formData.walletName,
      isDefault: paymentMethods.length === 0,
      status: "active",
    };

    setPaymentMethods((prev) => [...prev, newMethod]);
    setShowSuccess(true);

    // Reset form
    setFormData({
      momoType: "",
      phoneNumber: "",
      providerName: "",
      walletName: "",
    });
    setCurrentStep(1);

    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleCancel = () => {
    setShowSetupModal(false);
    setShowConfirmation(false);
    setShowEditModal(false);
    setShowDeleteConfirm(false);
    setCurrentStep(1);
    setFormData({
      momoType: "",
      phoneNumber: "",
      providerName: "",
      walletName: "",
    });
    setEditingMethod(null);
    setDeletingMethod(null);
  };

  const handleEdit = (method) => {
    console.log("Edit clicked for method:", method);
    setEditingMethod(method);
    setFormData({
      momoType: momoProviders.find((p) => p.name === method.provider)?.id || "",
      phoneNumber: method.number,
      providerName: method.provider,
      walletName: method.walletName || "",
    });
    setShowEditModal(true);
    console.log("Edit modal state set to true");
  };

  const handleDelete = (method) => {
    console.log("Delete clicked for method:", method);
    setDeletingMethod(method);
    setShowDeleteConfirm(true);
    console.log("Delete modal state set to true");
  };

  const handleSetDefault = (methodId) => {
    console.log("Set default clicked for method ID:", methodId);
    setPaymentMethods((prev) =>
      prev.map((method) => ({
        ...method,
        isDefault: method.id === methodId,
      }))
    );
    console.log("Default payment method updated");
  };

  const handleEditSave = () => {
    if (
      editingMethod &&
      formData.phoneNumber &&
      formData.providerName &&
      formData.walletName
    ) {
      setPaymentMethods((prev) =>
        prev.map((method) =>
          method.id === editingMethod.id
            ? {
                ...method,
                provider: formData.providerName,
                number: formData.phoneNumber,
                walletName: formData.walletName,
              }
            : method
        )
      );
      setShowEditModal(false);
      setEditingMethod(null);
      setFormData({
        momoType: "",
        phoneNumber: "",
        providerName: "",
        walletName: "",
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (deletingMethod) {
      setPaymentMethods((prev) =>
        prev.filter((method) => method.id !== deletingMethod.id)
      );
      setShowDeleteConfirm(false);
      setDeletingMethod(null);
    }
  };

  const handlePayout = (payoutData) => {
    console.log('Payout requested:', payoutData);
    // Here you would typically make an API call to process the payout
    // For now, we'll just log it and could add it to payout history
  };

  const canAddMore = paymentMethods.length < 5;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Account Status Modal - Blocks all interactions when account is under review */}
      <AccountStatusModal />
      
      {/* Blur and disable content when blocked */}
      <div className={isBlocked ? "pointer-events-none opacity-30 blur-sm transition-all duration-300" : "transition-all duration-300"}>
        <AuthNavbar />
        <div className="px-4 pt-24 pb-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl font-bold text-gray-900 lg:text-3xl">
            Withdraw Commission
          </h1>
          <p className="mt-2 text-sm text-gray-600 lg:text-base">
            Manage and withdraw your earned commissions
          </p>
          {/* Test buttons for debugging */}
          <div className="flex gap-2 mt-4"></div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:gap-8 lg:grid-cols-3">
          {/* Left Section: Commission Balance and Payment Methods */}
          <div className="order-2 space-y-4 lg:space-y-6 lg:order-1">
            {/* Current Commission Balance */}
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm lg:p-6">
              <h2 className="mb-3 text-base font-semibold text-gray-900 lg:mb-4 lg:text-lg">
                Current Commission Balance
              </h2>
              <div className="text-center">
                <div className="mb-2 text-3xl font-bold text-emerald-600 lg:text-4xl">
                  $132.45
                </div>
                <p className="text-xs text-gray-600 lg:text-sm">Transferable Amount</p>
              </div>
            </div>

            {/* Payment Methods List */}
            <PaymentMethodsList
              paymentMethods={paymentMethods}
              canAddMore={canAddMore}
              onAddMethod={handleSetupClick}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onSetDefault={handleSetDefault}
            />

            {/* Additional Payout Methods */}
            <div className="p-3 bg-gray-100 rounded-lg lg:p-4">
              <p className="text-xs text-center text-gray-600 lg:text-sm">
                To add additional payout methods, please visit your account
                settings.
              </p>
            </div>
          </div>

          {/* Payout Section - Full width on mobile, spans 2 columns on desktop */}
          <div className="order-1 lg:order-2 lg:col-span-2">
            <PayoutSection
              paymentMethods={paymentMethods}
              onPayout={handlePayout}
            />
          </div>
        </div>

        {/* Payout History Section */}
        <div className="mt-6 lg:mt-8">
          <PayoutHistory
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            payoutHistory={payoutHistory}
          />
        </div>
        </div>
      </div>

      {/* Modals - Outside blocking div so they remain functional */}
      <SetupModal
        show={showSetupModal}
        currentStep={currentStep}
        formData={formData}
        setFormData={setFormData}
        momoProviders={momoProviders}
        onNext={handleNext}
        onBack={handleBack}
        onCancel={handleCancel}
      />

      <EditModal
        show={showEditModal}
        editingMethod={editingMethod}
        formData={formData}
        setFormData={setFormData}
        momoProviders={momoProviders}
        onSave={handleEditSave}
        onCancel={handleCancel}
      />

      <DeleteConfirmModal
        show={showDeleteConfirm}
        deletingMethod={deletingMethod}
        onConfirm={handleDeleteConfirm}
        onCancel={handleCancel}
      />

      <ConfirmationModal
        show={showConfirmation}
        formData={formData}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <SuccessModal
        show={showSuccess}
        formData={formData}
        onClose={() => setShowSuccess(false)}
      />
    </div>
  );
}
